import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";

import global from "../../scss/dhp.scss";

import { EditorContext } from "../../containers/editor/EditorLayout";
import { CHART, GROUP_WIDGET, TYPE_INFOGRAPHIC, TYPE_PROPOSAL } from "../../constants/editor";
import { getClassListToString, renderChartFromConfig } from "../../_helpers/utils";
import useReplaceDuplicateShapeId from "../../hooks/useReplaceDuplicateShapeId";

let style = Object.assign({}, global);

const Block = props => {
  let { blockNodes, backgroundColors, backgroundImages, widgets, dimension, documentType } = props?.docPagesMeta
    ? { ...props.docPagesMeta }
    : useContext(EditorContext);

  const isDocumentAnalyticsEnabled = false && ["preview", "present"].includes(props?.source);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const safariBugfixCss = isSafari ? { willChange: "filter" } : {};
  const { replaceDuplicateIdFromSVG, repalceDuplicateIdFromFrame } = useReplaceDuplicateShapeId();

  // Replace duplicate id after widget modification
  useEffect(() => {
    replaceDuplicateIdFromSVG();
    repalceDuplicateIdFromFrame();
  }, [widgets]);

  // Replace duplicate id for shape use in shape border
  useEffect(() => {
    replaceDuplicateIdFromSVG();
    repalceDuplicateIdFromFrame();
  }, []);

  return (
    <div
      style={
        [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)
          ? { transformOrigin: "0px 0px", transform: `scale(${props.scaleFactor})` }
          : { height: `${(props.figureWidth / dimension.width) * dimension.height}px` }
      }>
      {blockNodes.map(
        blockNode =>
          blockNode.pageId === props.pageNode.pageId && (
            <div
              key={blockNode.blockId}
              {...(isDocumentAnalyticsEnabled && {
                id: `section-${blockNode.blockId}`
              })}
              className= "section"
              style={
                [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)
                  ? blockNode.style
                  : props.origin === "notification"
                  ? {
                      ...blockNode.style,
                      transformOrigin: `${(props.figureWidth - dimension.width * props.scaleFactor) / 2}px 0px`,
                      transform: `scale(${props.scaleFactor})`,
                    }
                  : { ...blockNode.style, transformOrigin: "0px 0px", transform: `scale(${props.scaleFactor})` }
              }>
              {backgroundColors.map(
                (backgroundColor, i) =>
                  backgroundColor.blockId === blockNode.blockId && <div key={i} style={backgroundColor.style}></div>
              )}

              {backgroundImages.map(
                (backgroundImage, i) =>
                  backgroundImage.blockId === blockNode.blockId && <div key={i} style={backgroundImage.style}></div>
              )}

              {widgets.map(
                widget =>
                  widget.blockId === blockNode.blockId && (
                    <div
                      key={widget.id}
                      {...(widget.data["data-asset-type"] !== GROUP_WIDGET && { "data-id": widget.id })}
                      {...{ "data-class": "dhp-root-widgets" }}
                      {...(props?.isPreviewPresentMode && widget.data)}
                      {...(props?.isPreviewPresentMode && { className: getClassListToString(widget.classLists) })}
                      {...(!props?.isPreviewPresentMode && { className: "dhp-widgets" })}
                      {...(!props?.isPreviewPresentMode &&
                        widget.classLists.includes("dhp-widget-hyperlinked") && {
                          className: "dhp-widgets dhp-widget-hyperlinked",
                        })}
                      {...(!props?.isPreviewPresentMode && widget.data)}
                      style={{ ...widget.style, ...safariBugfixCss, display: props?.origin === "play" ? "none" : "" }}
                      dangerouslySetInnerHTML={{
                        __html:
                          widget.data["data-asset-type"] === CHART && !["preview", "present"].includes(props?.source)
                            ? `<img style="width: 100%" src=${widget.chartThumb} />`
                            : widget.innerHTML,
                      }}
                      {...(props.isPresent ? { id: widget.id } : {})}
                      {...(props?.source === "preview" &&
                        widget.data["data-asset-type"] === GROUP_WIDGET && { "data-id": widget.id })}></div>
                  )
              )}
            </div>
          )
      )}
    </div>
  );
};
Block.propTypes = {
  scaleFactor: PropTypes.number.isRequired,
  figureWidth: PropTypes.number.isRequired,
  pageNode: PropTypes.object,
  docPagesMeta: PropTypes.object,
  isPreviewPresentMode: PropTypes.bool,
  source: PropTypes.string,
};

const SingleBlock = props => {
  let { backgroundColors, backgroundImages, widgets, dimension } = props?.docPagesMeta
    ? { ...props.docPagesMeta }
    : useContext(EditorContext);

  const isDocumentAnalyticsEnabled = false && ["preview", "present"].includes(props?.source);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const safariBugfixCss = isSafari ? { willChange: "filter" } : {};
  const { replaceDuplicateIdFromSVG, repalceDuplicateIdFromFrame } = useReplaceDuplicateShapeId();

  // Replace duplicate id after widget modification
  useEffect(() => {
    replaceDuplicateIdFromSVG();
    repalceDuplicateIdFromFrame();
  }, [widgets]);

  // Replace duplicate id for shape use in shape border
  useEffect(() => {
    replaceDuplicateIdFromSVG();
    repalceDuplicateIdFromFrame();
  }, []);

  return (
    <div style={{ height: `${(props.figureWidth / dimension.width) * parseFloat(props.blockNode.style.height)}px` }}>
      <div
        key={props.blockNode.blockId}
        {...(isDocumentAnalyticsEnabled && {
          id: `section-${props.blockNode.blockId}`,
          className: "section",
        })}
        style={{ ...props.blockNode.style, transformOrigin: "0px 0px", transform: `scale(${props.scaleFactor})` }}>
        {backgroundColors.map(
          (backgroundColor, i) =>
            backgroundColor.blockId === props.blockNode.blockId && <div key={i} style={backgroundColor.style}></div>
        )}

        {backgroundImages.map(
          (backgroundImage, i) =>
            backgroundImage.blockId === props.blockNode.blockId && <div key={i} style={backgroundImage.style}></div>
        )}

        {widgets.map(
          widget =>
            widget.blockId === props.blockNode.blockId && (
              <div
                key={widget.id}
                {...(widget.data["data-asset-type"] !== GROUP_WIDGET && { "data-id": widget.id })}
                {...(props?.isPreviewPresentMode && widget.data)}
                {...(props?.isPreviewPresentMode && { className: getClassListToString(widget.classLists) })}
                {...(!props?.isPreviewPresentMode && { className: "dhp-widgets" })}
                {...(!props?.isPreviewPresentMode &&
                  widget.classLists.includes("dhp-widget-hyperlinked") && {
                    className: "dhp-widgets dhp-widget-hyperlinked",
                  })}
                {...(!props?.isPreviewPresentMode && widget.data)}
                style={{ ...widget.style, ...safariBugfixCss }}
                dangerouslySetInnerHTML={{
                  __html:
                    widget.data["data-asset-type"] === CHART && !["preview", "present"].includes(props?.source)
                      ? `<img style="width: 100%" src=${widget.chartThumb} />`
                      : widget.innerHTML,
                }}
                {...(props.isPresent ? { id: widget.id } : {})}
                {...(props?.source === "preview" &&
                  widget.data["data-asset-type"] === GROUP_WIDGET && { "data-id": widget.id })}></div>
            )
        )}
      </div>
    </div>
  );
};
SingleBlock.propTypes = {
  scaleFactor: PropTypes.number.isRequired,
  figureWidth: PropTypes.number.isRequired,
  blockNode: PropTypes.object,
  docPagesMeta: PropTypes.object,
  isPreviewPresentMode: PropTypes.bool,
  source: PropTypes.string,
};

const CanvasPageHtmlGenaretor = props => {
  let { dimension, documentType, blockNodes, widgets } = props?.docPagesMeta?.dimension
    ? { ...props.docPagesMeta }
    : useContext(EditorContext);

  let scaleFactor;

  if (props?.origin === "notification" && [TYPE_PROPOSAL].includes(documentType)) {
    const blockWidth = parseFloat(blockNodes[0].style.width);
    const blockHeight = parseFloat(blockNodes[0].style.height);

    scaleFactor = Math.max(props.figureWidth / blockWidth, props.figureWidth / blockHeight);
  } else if (props?.origin === "notification") {
    scaleFactor = Math.max(props.figureWidth / dimension.width, props.figureWidth / dimension.height);
  } else {
    scaleFactor = props.figureWidth / dimension.width;
  }

  let figureHeight = props?.figureHeight
    ? props?.figureHeight
    : ([TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) && ["aiTranslate"].includes(props.actionPont)) ||
      ([TYPE_PROPOSAL].includes(documentType) && ["download"].includes(props.actionPont))
    ? (props.figureWidth / parseFloat(blockNodes[props.pageNode.blockIdx].style.width)) *
      parseFloat(blockNodes[props.pageNode.blockIdx].style.height)
    : (props.figureWidth / dimension.width) * dimension.height;

  useEffect(() => {
    if (widgets && props?.chartRenderingRef) {
      const domRef = window.document.querySelector(props.chartRenderingRef);
      if (domRef) {
        renderChartFromConfig(domRef, "dhp-widgets", widgets);
      }
    }
  }, [props?.chartRenderingRef]);

  return (
    <React.Fragment key={props.blockIndex ? props.blockIndex : props.pageNode.pageIdx}>
      <div className={props.className} id={props?.id} style={props?.style}>
        <figure
          className={style["mb-0"]}
          style={{
            height: `${figureHeight}px`,
            width: `${props.figureWidth}px`,
            backgroundColor: ["play", "notification"].includes(props?.origin) ? "#ffffff" : "",
          }}>
          {[TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) &&
            (!props.actionPont || !["link", "aiTranslate", "download"].includes(props.actionPont)) && (
              <div>
                <Block
                  scaleFactor={scaleFactor}
                  pageNode={props.pageNode}
                  figureWidth={props.figureWidth}
                  docPagesMeta={props.docPagesMeta}
                  isPreviewPresentMode={props?.isPreviewPresentMode}
                  isPresent={props?.isPresent}
                  isPublishedPage={props?.isPublishedPage}
                  origin={props?.origin}
                  source={props?.source}
                />
              </div>
            )}

          {[TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) &&
            ["link", "aiTranslate", "download"].includes(props.actionPont) && (
              <div>
                <SingleBlock
                  scaleFactor={scaleFactor}
                  blockNode={props.pageNode}
                  figureWidth={props.figureWidth}
                  docPagesMeta={props.docPagesMeta}
                  isPreviewPresentMode={props?.isPreviewPresentMode}
                  isPresent={props?.isPresent}
                  isPublishedPage={props?.isPublishedPage}
                  source={props?.source}
                />
              </div>
            )}

          {![TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) && (
            <Block
              scaleFactor={scaleFactor}
              pageNode={props.pageNode}
              figureWidth={props.figureWidth}
              docPagesMeta={props.docPagesMeta}
              isPreviewPresentMode={props?.isPreviewPresentMode}
              isPresent={props?.isPresent}
              origin={props?.origin}
              source={props?.source}
            />
          )}
        </figure>

        {props.pageType && props.pageType !== "Block" && (
          <span>
            {props.pageType} {props.blockIndex !== undefined ? props.blockIndex + 1 : props.pageNode.pageIdx + 1}
          </span>
        )}

        {props.pageType && props.pageType === "Block" && (
          <span>
            {props.pageType} {props.blockIndex + 1}
            {props.pageNode.blockName ? `: ${props.pageNode.blockName}` : ""}
          </span>
        )}
      </div>
    </React.Fragment>
  );
};
CanvasPageHtmlGenaretor.propTypes = {
  className: PropTypes.string,
  figureWidth: PropTypes.number.isRequired,
  figureHeight: PropTypes.number,
  pageNode: PropTypes.object,
  pageType: PropTypes.string,
  docPagesMeta: PropTypes.object,
  id: PropTypes.string,
  style: PropTypes.object,
  isPreviewPresentMode: PropTypes.bool,
  actionPont: PropTypes.string,
  source: PropTypes.string,
};

export default CanvasPageHtmlGenaretor;
